import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { TIPOS_FORNECEDOR, STATUS_RELACIONAMENTO, STATUS_RELACIONAMENTO_LABELS } from '@/pages/painel/lib/inteligencia';

interface EmpresaOpt {
  id: number;
  nome: string;
  cidade: string | null;
  uf: string | null;
}

interface Fornecedor {
  id?: number;
  empresa_id: number | null;
  tipo_fornecedor: string[];
  categorias: string[];
  marcas: string[];
  produtos: string[];
  politica_revenda: string | null;
  tabela_atualizada_em: string | null;
  desconto: number | null;
  impostos: string | null;
  frete: string | null;
  prazo: string | null;
  pedido_minimo: string | null;
  condicao_pagamento: string | null;
  regiao: string | null;
  score_fornecedor: number | null;
  observacao: string | null;
  status_relacionamento: string;
}

interface FornecedorFormProps {
  empresas: EmpresaOpt[];
  inicial: Fornecedor | null;
  password: string;
  onSaved: () => void;
  onCancel: () => void;
}

function splitTags(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

export default function FornecedorForm({ empresas, inicial, password, onSaved, onCancel }: FornecedorFormProps) {
  const [empresaId, setEmpresaId] = useState<string>(inicial?.empresa_id != null ? String(inicial.empresa_id) : '');
  const [tipos, setTipos] = useState<string[]>(inicial?.tipo_fornecedor ?? []);
  const [categorias, setCategorias] = useState((inicial?.categorias ?? []).join(', '));
  const [marcas, setMarcas] = useState((inicial?.marcas ?? []).join(', '));
  const [produtos, setProdutos] = useState((inicial?.produtos ?? []).join(', '));
  const [politicaRevenda, setPoliticaRevenda] = useState(inicial?.politica_revenda ?? '');
  const [desconto, setDesconto] = useState(inicial?.desconto != null ? String(inicial.desconto) : '');
  const [impostos, setImpostos] = useState(inicial?.impostos ?? '');
  const [frete, setFrete] = useState(inicial?.frete ?? '');
  const [prazo, setPrazo] = useState(inicial?.prazo ?? '');
  const [pedidoMinimo, setPedidoMinimo] = useState(inicial?.pedido_minimo ?? '');
  const [condicaoPagamento, setCondicaoPagamento] = useState(inicial?.condicao_pagamento ?? '');
  const [regiao, setRegiao] = useState(inicial?.regiao ?? '');
  const [score, setScore] = useState(inicial?.score_fornecedor != null ? String(inicial.score_fornecedor) : '');
  const [statusRel, setStatusRel] = useState(inicial?.status_relacionamento ?? 'ativo');
  const [observacao, setObservacao] = useState(inicial?.observacao ?? '');
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const toggleTipo = (k: string) => {
    setTipos((prev) => (prev.includes(k) ? prev.filter((t) => t !== k) : [...prev, k]));
  };

  const salvar = async () => {
    if (!empresaId) {
      setErro('Selecione a empresa vinculada ao fornecedor.');
      return;
    }
    setBusy(true);
    setErro(null);
    try {
      const { data, error } = await supabase.functions.invoke('filas-admin', {
        body: {
          action: 'fornecedor_salvar',
          password,
          id: inicial?.id ?? undefined,
          empresa_id: Number(empresaId),
          tipo_fornecedor: tipos,
          categorias: splitTags(categorias),
          marcas: splitTags(marcas),
          produtos: splitTags(produtos),
          politica_revenda: politicaRevenda.trim() || null,
          desconto: desconto !== '' ? Number(desconto) : null,
          impostos: impostos.trim() || null,
          frete: frete.trim() || null,
          prazo: prazo.trim() || null,
          pedido_minimo: pedidoMinimo.trim() || null,
          condicao_pagamento: condicaoPagamento.trim() || null,
          regiao: regiao.trim() || null,
          score_fornecedor: score !== '' ? Number(score) : null,
          observacao: observacao.trim() || null,
          status_relacionamento: statusRel,
        },
      });
      if (error) throw new Error(await extractErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      onSaved();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao salvar fornecedor.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all bg-white';
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">{inicial ? 'Editar fornecedor' : 'Novo fornecedor'}</h3>
            <p className="text-xs text-gray-500">Vínculo com empresa existente + dados comerciais.</p>
          </div>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className={labelCls}>Empresa vinculada *</label>
            <select value={empresaId} onChange={(e) => setEmpresaId(e.target.value)} className={inputCls}>
              <option value="">Selecionar empresa...</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>{e.nome}{e.cidade ? ` — ${e.cidade}` : ''}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>Tipo de fornecedor</label>
            <div className="flex flex-wrap gap-2">
              {TIPOS_FORNECEDOR.map((t) => (
                <button
                  key={t.key}
                  onClick={() => toggleTipo(t.key)}
                  className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                    tipos.includes(t.key) ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]/40'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Categorias (separadas por vírgula)</label>
              <input value={categorias} onChange={(e) => setCategorias(e.target.value)} placeholder="cadeiras, mesas" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Marcas</label>
              <input value={marcas} onChange={(e) => setMarcas(e.target.value)} placeholder="Cavaletti, Frisokar" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Produtos</label>
              <input value={produtos} onChange={(e) => setProdutos(e.target.value)} placeholder="cadeira, estação, divisória" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Desconto (%)</label>
              <input type="number" value={desconto} onChange={(e) => setDesconto(e.target.value)} placeholder="0" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Prazo</label>
              <input value={prazo} onChange={(e) => setPrazo(e.target.value)} placeholder="15 dias" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Score fornecedor (0–100)</label>
              <input type="number" value={score} onChange={(e) => setScore(e.target.value)} placeholder="—" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Frete</label>
              <input value={frete} onChange={(e) => setFrete(e.target.value)} placeholder="CIF / FOB" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Pedido mínimo</label>
              <input value={pedidoMinimo} onChange={(e) => setPedidoMinimo(e.target.value)} placeholder="R$ 5.000" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Região de atuação</label>
              <input value={regiao} onChange={(e) => setRegiao(e.target.value)} placeholder="Pernambuco" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Condição de pagamento</label>
              <input value={condicaoPagamento} onChange={(e) => setCondicaoPagamento(e.target.value)} placeholder="28/56 dias" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Impostos relevantes</label>
              <input value={impostos} onChange={(e) => setImpostos(e.target.value)} placeholder="ICMS, IPI" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Política de revenda</label>
            <input value={politicaRevenda} onChange={(e) => setPoliticaRevenda(e.target.value)} placeholder="Revenda autorizada, proteção de preço" className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Observação</label>
            <textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} rows={2} className={inputCls} maxLength={500} />
          </div>

          <div>
            <label className={labelCls}>Status do relacionamento</label>
            <select value={statusRel} onChange={(e) => setStatusRel(e.target.value)} className={inputCls}>
              {STATUS_RELACIONAMENTO.map((s) => (
                <option key={s.key} value={s.key}>{STATUS_RELACIONAMENTO_LABELS[s.key]}</option>
              ))}
            </select>
          </div>

          {erro && <p className="text-sm text-red-600">{erro}</p>}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={salvar}
              disabled={busy}
              className="inline-flex items-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Salvar fornecedor
            </button>
            <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700 px-4 py-2.5 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}